<template>
  <div v-if="showChannels && channels.length > 0">
    <span class="dropdown">
      <select id="channel-filter" v-model="selectedChannelId">
        <option value="">All Channels</option>
        <option v-for="channel in sortedChannels" :key="channel.id" :value="channel.id">
          {{ channel.name }}
        </option>
      </select>
    </span>
    <button class="clear-filter" v-if="selectedChannelId" @click="clearSelectedChannel">Clear Filter</button>
  </div>
  <LoadingIndicator v-if="isLoading">Loading videos...</LoadingIndicator>
  <div v-else class="table-container">
    <table>
      <thead>
        <tr>
          <th>Visibility</th>
          <SortableListHeader
            column="title" 
            :current-sort="sortColumn" 
            default-direction="asc"
            @sort="handleSort"
          >
            Title
          </SortableListHeader>
          <SortableListHeader 
            v-if="showChannels"
            column="channel" 
            :current-sort="sortColumn"
            default-direction="asc"
            @sort="handleSort"
          >
            Channel
          </SortableListHeader>
          <SortableListHeader 
            column="video_stream" 
            :current-sort="sortColumn"
            @sort="handleSort"
          >
            Video Stream
          </SortableListHeader>
          <SortableListHeader 
            column="trailer_stream" 
            :current-sort="sortColumn"
            @sort="handleSort"
          >
            Trailer Stream
          </SortableListHeader>
          <SortableListHeader 
            column="created" 
            :current-sort="sortColumn"
            @sort="handleSort"
          >
            Created
          </SortableListHeader>
          <SortableListHeader 
            column="modified" 
            :current-sort="sortColumn"
            @sort="handleSort"
          >
            Last Modified
          </SortableListHeader>
          <th>Actions</th>
        </tr>
      </thead>
      <tbody>
        <tr v-if="sortedVideos.length === 0">
          <td :colspan="showChannels ? 8 : 7">No videos found.</td>
        </tr>
        <tr 
          v-for="video in sortedVideos" 
          :key="video.id" 
          @click="handleRowClick($event, video)" 
          :class="{ clickable: isRowClickable() }"
          :title="isRowClickable() ? `View &quot;${video.title}&quot;` : null"
        >
          <td>
            <span :class="`availability-badge ${availability(video).toLowerCase()}`">
              {{ formatStatus(availability(video)) }}
            </span>
          </td>
          <td class="overflow">
            <b :title="video.title">{{ video.title }}</b>
          </td>
          <td v-if="showChannels">
            <router-link 
              v-if="video.channel_id" 
              :to="`/channels/${video.channel_id}`" 
              :title="`View Channel &quot;${video.channel_name}&quot;`"
              class="link-button"
            >
              {{ video.channel_name }}
            </router-link>
            <span v-else class="no-channel">No Channel</span>
          </td>
          <td>
            <VideoFileStreamStatus :videoFile="video.video_file" />
          </td>
          <td>
            <VideoFileStreamStatus :videoFile="video.trailer_file" />
          </td>
          <td :title="formatDatetime(video.created_at)">
            {{ formatDate(video.created_at) }}
          </td>
          <td>
            {{ formatDatetime(video.last_modified) }}
          </td>
          <td>
            <span class="actions">
              <button 
                @click="copyShareLink(video)" 
                class="action-button view"
                title="Copy Share Link"
                v-if="canShareVideo(props.organizationId) && video.share_links?.length > 0"
              >
                <font-awesome-icon icon="link" />
              </button>
              <button 
                @click="generateShareLink(video)" 
                class="action-button edit"
                title="Create Share Link"
                v-if="!isSuperuser() && canShareVideo(props.organizationId) && video.share_links?.length === 0"
              >
                <font-awesome-icon icon="share-from-square" />
              </button>
              <router-link 
                :to="`/videos/${video.id}/edit`" 
                class="action-button edit"
                title="Edit"
                v-if="canEditVideo(props.organizationId)"
              >
                <font-awesome-icon icon="edit" />
              </router-link>
              <button 
                @click="confirmDelete(video)" 
                class="action-button delete"
                title="Delete"
                v-if="canDeleteVideo(props.organizationId)"
              >
                <font-awesome-icon icon="trash" />
              </button>
            </span>
          </td>
        </tr>
      </tbody>
    </table>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue';
import { useRouter } from 'vue-router';
import { useQuery, useMutation, useQueryClient } from '@tanstack/vue-query';
import { FontAwesomeIcon } from '@/scripts/icons';
import { getAllVideos, createShareLink, deleteVideo, videoKeys } from '@/scripts/api/videoApi';
import { getAllChannels, channelKeys, getChannelVideos } from '@/scripts/api/channelApi';
import { canViewVideo, canEditVideo, canDeleteVideo, canShareVideo, isSuperuser } from '@/scripts/permissions';
import { copyShareUrl } from '@/scripts/services/shareLinkService';
import { handleApiError, isInteractiveElement, showConfirmDialog, showAlert } from '@/scripts/shared-utils';
import { formatDate, formatDatetime, formatStatus } from '@/scripts/shared-utils';
import SortableListHeader from '@/components/SortableListHeader.vue';
import VideoFileStreamStatus from '@/components/VideoFileStreamStatus.vue';
import LoadingIndicator from '@/components/LoadingIndicator.vue';
import { getStreamStatusSortOrder } from '@/scripts/shared-video';

const router = useRouter();

const props = defineProps({
  channelId: {
    type: String,
    default: null
  },
  organizationId: {
    type: String,
    default: null
  },
  showChannels: { 
    type: Boolean, 
    default: false 
  },
  filter: {
    type: Function,
    default: null
  }
});

const queryClient = useQueryClient();
const selectedChannelId = ref('');
const sortColumn = ref('modified');
const sortDirection = ref('desc');

const { data: channels } = useQuery({
  queryKey: channelKeys.getAllChannels(),
  queryFn: getAllChannels,
  enabled: computed(() => props.showChannels && !props.channelId),
  staleTime: 60000,
  placeholderData: [],
  onError: (error) => handleApiError(error, 'fetching channels')
});

const videosQueryKey = computed(() => {
  return props.channelId ? channelKeys.getChannelVideos(props.channelId) : videoKeys.getAllVideos();
});

const videosQueryFunction = () => {
  if (props.channelId) {
    return getChannelVideos(props.channelId);
  }
  return getAllVideos();
}

const { data: videos, isLoading } = useQuery({
  queryKey: videosQueryKey,
  queryFn: videosQueryFunction,
  staleTime: 60000,
  placeholderData: [],
  onError: (error) => handleApiError(error, 'fetching videos')
});

const createShareLinkMutation = useMutation({
  mutationFn: (videoId) => createShareLink(videoId, { visibility: 'Public' }),
  onSuccess: async (data) => {
    await copyShareUrl(data.short_id);
    showAlert(`New share link copied to clipboard`);
    await queryClient.invalidateQueries({ queryKey: videoKeys.getAllVideos() });
    if (props.channelId) {
      await queryClient.invalidateQueries({ queryKey: channelKeys.getChannelVideos(props.channelId) });
    }
  },
  onError: (error) => {
    handleApiError(error, 'creating share link');
  },
});

const deleteVideoMutation = useMutation({
  mutationFn: deleteVideo,
  onSuccess: () => {
    queryClient.invalidateQueries({ queryKey: videoKeys.getAllVideos() });
    if (props.channelId) {
      queryClient.invalidateQueries({ queryKey: channelKeys.getChannelVideos(props.channelId) });
    }
  },
  onError: (error) => {
    handleApiError(error, 'deleting video');
  },
});

const filteredVideos = computed(() => {
  let currentVideos = videos.value ? [...videos.value] : [];
  if (props.filter) {
    currentVideos = currentVideos.filter(props.filter);
  }
  if (selectedChannelId.value) {
    currentVideos = currentVideos.filter(video => video.channel_id === selectedChannelId.value);
  }
  return currentVideos;
});

const sortedVideos = computed(() => {
  let sortedVideos = [...filteredVideos.value];
  
  sortedVideos.sort((a, b) => {
    let comparison = 0;
    let lhs, rhs;
    
    switch (sortColumn.value) {
      case 'title':
        lhs = a.title || '';
        rhs = b.title || '';
        comparison = lhs.localeCompare(rhs);
        break;
        
      case 'channel':
        lhs = a.channel?.name || '';
        rhs = b.channel?.name || '';
        comparison = lhs.localeCompare(rhs);
        break;
        
      case 'video_stream':
        lhs = getStreamStatusSortOrder(a.video_file?.stream_status || '');
        rhs = getStreamStatusSortOrder(b.video_file?.stream_status || '');
        comparison = lhs - rhs;
        break;
        
      case 'trailer_stream':
        lhs = getStreamStatusSortOrder(a.trailer_file?.stream_status || '');
        rhs = getStreamStatusSortOrder(b.trailer_file?.stream_status || '');
        comparison = lhs - rhs;
        break;
        
      case 'created':
        lhs = new Date(a.created_at || 0).getTime();
        rhs = new Date(b.created_at || 0).getTime();
        comparison = lhs - rhs;
        break;
        
      case 'modified':
      default:
        lhs = new Date(a.last_modified || 0).getTime();
        rhs = new Date(b.last_modified || 0).getTime();
        comparison = lhs - rhs;
        break;
    }
    
    return sortDirection.value === 'asc' ? comparison : -comparison;
  });
  
  return sortedVideos;
});

const sortedChannels = computed(() => {
  return [...channels.value].sort((a, b) => {
    return a.name.localeCompare(b.name);
  });
});

const clearSelectedChannel = () => {
  selectedChannelId.value = '';
};

const handleSort = (sortData) => {
  sortColumn.value = sortData.column;
  sortDirection.value = sortData.direction;
};

const isRowClickable = () => {
  return canViewVideo(props.organizationId);
}

const handleRowClick = (event, video) => {
  if (!isInteractiveElement(event) && isRowClickable()) {
    router.push(`/videos/${video.id}`);
  }
};

const copyShareLink = async (video) => {
  if (video.share_links?.length > 0) {
    await copyShareUrl(video.share_links[0].short_id);
    showAlert(`Share link copied to clipboard`);
  }
};

const generateShareLink = (video) => {
  createShareLinkMutation.mutate(video.id);
};

const confirmDelete = async (video) => {
  if (await showConfirmDialog('Are you sure you want to delete this video?')) {
    deleteVideoMutation.mutate(video.id);
  }
};

const availability = (video) => {
  if (video.share_links?.length > 0) return 'ShareLink';
  return 'Organization';
}
</script>

<style scoped>
@import '@/styles/shared-list.css';
@import '@/styles/videos-list.css';
</style>